import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useBooking } from '../booking/useBooking';
import { BookingFilters } from '../components/BookingFilters';
import { FirstAvailableResult } from '../components/FirstAvailableResult';
import { DateTimeSelector } from '../components/DateTimeSelector';
import { BookingConfirmation } from '../components/BookingConfirmation';

type Step = 'select' | 'confirm';

/**
 * In-app appointment creation — a faithful web port of the mobile booking flow:
 * cascading auto-filtering dropdowns, then "Primul loc disponibil" (first
 * available) or "Selectați ziua și ora" (manual), then a confirmation step.
 */
export function NewAppointmentPage() {
  const { t } = useTranslation();
  const booking = useBooking();
  const [step, setStep] = useState<Step>('select');

  const { mode, chooseMode, canChooseMode, resolvedSlot } = booking;

  if (step === 'confirm' && resolvedSlot) {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-5">
        <PageHeader title={t('booking.title')} subtitle={t('booking.confirm_subtitle')} />
        <BookingConfirmation booking={booking} slot={resolvedSlot} onBack={() => setStep('select')} />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-5">
      <PageHeader title={t('booking.title')} subtitle={t('booking.subtitle')} />

      <Card className="flex flex-col gap-5 p-5">
        <BookingFilters booking={booking} />

        {canChooseMode && (
          <div className="flex flex-col gap-3 border-t border-muted-200 pt-5">
            <span className="text-sm font-semibold text-ink-soft">{t('booking.mode_title')}</span>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <ModeCard
                active={mode === 'first'}
                label={t('booking.mode_first')}
                description={t('booking.mode_first_hint')}
                onClick={() => chooseMode('first')}
              />
              <ModeCard
                active={mode === 'manual'}
                label={t('booking.mode_manual')}
                description={t('booking.mode_manual_hint')}
                onClick={() => chooseMode('manual')}
              />
            </div>
          </div>
        )}

        {canChooseMode && mode === 'first' && <FirstAvailableResult booking={booking} />}
        {canChooseMode && mode === 'manual' && <DateTimeSelector booking={booking} />}
      </Card>

      {resolvedSlot && (
        <Button onClick={() => setStep('confirm')} block size="lg">
          {t('booking.continue')}
        </Button>
      )}
    </div>
  );
}

interface ModeCardProps {
  active: boolean;
  label: string;
  description: string;
  onClick: () => void;
}

function ModeCard({ active, label, description, onClick }: ModeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col gap-1 rounded-input border p-3 text-left transition-colors',
        active ? 'border-primary bg-primary-light' : 'border-muted-300 bg-white hover:border-primary/50',
      )}
    >
      <span className={cn('text-sm font-bold', active ? 'text-primary' : 'text-ink')}>{label}</span>
      <span className="text-xs text-ink-mute">{description}</span>
    </button>
  );
}
